import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { unifiedUserService } from '../services/unifiedUserService';

// 统一用户资料接口
interface UserProfile {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  ageRange?: string;
  assessmentFor?: 'self' | 'family' | 'other';
  preferredContentLevel?: 'simple' | 'standard' | 'detailed';
  phone?: string;
  medicalHistory?: string;
  notes?: string;
  // 元数据
  createdAt?: string;
  updatedAt?: string;
  source?: 'api' | 'localStorage' | 'session' | 'auth' | 'default';
}

// 用户状态类型
type UserState = 'idle' | 'loading' | 'loaded' | 'error' | 'creating';

interface UserContextType {
  // 状态
  currentUser: UserProfile | null;
  userState: UserState;
  error: string | null;

  // 操作方法
  setCurrentUser: (user: UserProfile | null) => void;
  refreshUser: (userId?: string) => Promise<void>;
  switchUser: (userId: string) => Promise<void>;
  createTemporaryUser: (userData: Partial<UserProfile>) => UserProfile;
  clearUser: () => void;

  // 辅助方法
  getUserDisplayName: () => string;
  getUserAgeRange: () => string;
  isUserLoaded: () => boolean;
  canManageCurrentUser: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUserState] = useState<UserProfile | null>(null);
  const [userState, setUserState] = useState<UserState>('idle');
  const [error, setError] = useState<string | null>(null);

  const { carer, isAuthenticated, loading: authLoading } = useAuth();

  // 🔧 使用 ref 防止重复初始化
  const hasInitialized = useRef(false);

  // 🎯 统一用户ID解析策略
  const resolveUserId = useCallback((): string | null => {
    // 1. URL参数优先（明确指定的用户）
    const urlParams = new URLSearchParams(window.location.search);
    const urlUserId = urlParams.get('userId');
    if (urlUserId) {
      console.log('🔍 [UserContext] 使用URL参数中的用户ID:', urlUserId);
      return urlUserId;
    }

    // 2. sessionStorage中的当前用户ID
    const sessionUserId = sessionStorage.getItem('currentUserId');
    if (sessionUserId) {
      console.log('🔍 [UserContext] 使用session中的用户ID:', sessionUserId);
      return sessionUserId;
    }

    // 3. localStorage中选择的用户ID
    const selectedUserId = localStorage.getItem('selectedUserId');
    if (selectedUserId) {
      console.log('🔍 [UserContext] 使用localStorage中的用户ID:', selectedUserId);
      return selectedUserId;
    }

    // 如果所有方式都无法解析用户ID，返回null
    console.warn('⚠️ [UserContext] 无法解析用户ID - 所有数据源均为空');
    return null;
  }, []);

  // 🔄 加载用户信息
  const loadUser = useCallback(async (userId: string) => {
    setUserState('loading');
    setError(null);

    try {
      console.log('🔄 [UserContext] 开始加载用户信息:', userId);

      // ✅ 检查认证状态 - 所有用户都被平等对待，不再区分"本地"和"正式"用户
      // 用户类型应该通过查询数据库确定，而不是通过ID前缀
      if (!isAuthenticated) {
        console.log('⚠️ [UserContext] 用户未认证，创建临时用户配置');
        const tempUserProfile = createTemporaryUserProfile(userId);
        setCurrentUserState(tempUserProfile);
        setUserState('loaded');
        return;
      }

      console.log('✅ [UserContext] 已认证用户，从API加载用户信息')

      // 从统一用户服务获取用户记录
      const userRecord = await unifiedUserService.getUserRecord(userId);

      if (userRecord?.personalInfo) {
        const userProfile: UserProfile = {
          id: userRecord.id,
          name: userRecord.personalInfo.name,
          age: userRecord.personalInfo.age,
          gender: userRecord.personalInfo.gender,
          ageRange: calculateAgeRange(userRecord.personalInfo.age),
          assessmentFor: userRecord.personalInfo.assessmentFor || 'self',
          preferredContentLevel: userRecord.personalInfo.preferredContentLevel || 'standard',
          phone: userRecord.personalInfo.phone,
          medicalHistory: userRecord.personalInfo.medicalHistory,
          notes: userRecord.personalInfo.notes,
          createdAt: userRecord.createdAt,
          updatedAt: userRecord.updatedAt,
          source: 'api'
        };

        setCurrentUserState(userProfile);
        setUserState('loaded');

        // 缓存用户ID到sessionStorage
        sessionStorage.setItem('currentUserId', userId);

        console.log('✅ [UserContext] 用户信息加载成功:', userProfile.name);
        return;
      }

      // 用户不存在，报错而不是创建临时资料
      console.error('❌ [UserContext] 用户档案不存在:', userId);
      setError(`用户档案不存在: ${userId}`);
      setUserState('error');

    } catch (error) {
      console.error('❌ [UserContext] 用户信息加载失败:', error);
      setError(`加载用户信息失败: ${error instanceof Error ? error.message : '未知错误'}`);
      setUserState('error');
    }
  }, [isAuthenticated]); // ✅ 添加isAuthenticated依赖

  // 🔄 刷新用户信息
  const refreshUser = useCallback(async (userId?: string) => {
    const targetUserId = userId || resolveUserId();
    if (targetUserId) {
      await loadUser(targetUserId);
    } else {
      console.warn('⚠️ [UserContext] 刷新用户：无法确定用户ID');
      setCurrentUserState(null);
      setUserState('idle');
    }
  }, [resolveUserId, loadUser]);

  // 🔄 切换用户
  const switchUser = useCallback(async (userId: string) => {
    console.log('🔄 [UserContext] 切换用户:', userId);

    // 清除之前的用户状态
    setCurrentUserState(null);
    setError(null);

    // 更新存储的用户ID
    sessionStorage.setItem('currentUserId', userId);
    localStorage.setItem('selectedUserId', userId);

    // 加载新用户
    await loadUser(userId);
  }, [loadUser]);

  // 🔄 设置当前用户
  const setCurrentUser = useCallback((user: UserProfile | null) => {
    setCurrentUserState(user);
    if (user) {
      setUserState('loaded');
      sessionStorage.setItem('currentUserId', user.id);
    } else {
      setUserState('idle');
      sessionStorage.removeItem('currentUserId');
    }
  }, []);

  // 🔄 创建临时用户
  const createTemporaryUser = useCallback((userData: Partial<UserProfile>): UserProfile => {
    const tempUser: UserProfile = {
      id: userData.id || `temp_${Date.now()}`,
      name: userData.name || `临时用户_${userData.id?.slice(-4) || Math.random().toString(36).slice(-4)}`,
      age: userData.age,
      gender: userData.gender,
      ageRange: userData.age ? calculateAgeRange(userData.age) : 'unknown',
      assessmentFor: userData.assessmentFor || 'self',
      preferredContentLevel: userData.preferredContentLevel || 'standard',
      source: 'session'
    };

    console.log('🔧 [UserContext] 创建临时用户:', tempUser.name);
    return tempUser;
  }, []);

  // 🧹 清除用户状态
  const clearUser = useCallback(() => {
    setCurrentUserState(null);
    setUserState('idle');
    setError(null);
    sessionStorage.removeItem('currentUserId');
    localStorage.removeItem('selectedUserId');
    console.log('🧹 [UserContext] 已清除用户状态');
  }, []);

  // 📊 辅助方法
  const getUserDisplayName = useCallback(() => {
    return currentUser?.name || '未知用户';
  }, [currentUser?.name]);

  const getUserAgeRange = useCallback(() => {
    return currentUser?.ageRange || 'unknown';
  }, [currentUser?.ageRange]);

  const isUserLoaded = useCallback(() => {
    return userState === 'loaded' && currentUser !== null;
  }, [userState, currentUser]);

  const canManageCurrentUser = useCallback(() => {
    if (!isAuthenticated || !currentUser) return false;

    // 如果当前用户就是认证用户，可以管理
    if (currentUser.id === carer?.id) return true;

    // 专业照护者可以管理所有用户
    return carer?.role === 'professional';
  }, [isAuthenticated, currentUser, carer]);

  // 🎯 初始化用户状态
  useEffect(() => {
    const initializeUser = async () => {
      // 🔧 防止重复初始化
      if (hasInitialized.current) {
        return;
      }

      // 等待认证加载完成
      if (authLoading) {
        console.log('⏳ [UserContext] 等待认证加载完成...');
        return;
      }

      // 只在已认证时才初始化用户
      if (!isAuthenticated) {
        console.log('⚠️ [UserContext] 用户未认证，跳过初始化');
        return;
      }

      // 只在idle或error状态时才初始化
      if (userState !== 'idle' && userState !== 'error') {
        return;
      }

      const userId = resolveUserId();
      if (!userId) {
        console.log('⚠️ [UserContext] 无法解析用户ID');
        return;
      }

      // 标记为已初始化
      hasInitialized.current = true;
      console.log('🔄 [UserContext] 初始化用户，状态:', userState);
      await loadUser(userId);
    };

    initializeUser();
  }, [authLoading, isAuthenticated, userState, resolveUserId, loadUser]); // ✅ 完整依赖列表

  // 🎯 监听认证状态变化 - 只在真正登出时清除
  useEffect(() => {
    // 只有在从已认证变为未认证时才清除用户数据
    if (!isAuthenticated && userState === 'loaded') {
      console.log('🔐 [UserContext] 检测到登出，清除用户状态');
      hasInitialized.current = false; // 重置初始化标志
      clearUser();
    }
  }, [isAuthenticated, userState, clearUser]); // ✅ 完整依赖列表

  const value: UserContextType = {
    currentUser,
    userState,
    error,
    setCurrentUser,
    refreshUser,
    switchUser,
    createTemporaryUser,
    clearUser,
    getUserDisplayName,
    getUserAgeRange,
    isUserLoaded,
    canManageCurrentUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// 🔧 辅助函数
function calculateAgeRange(age?: number): string {
  if (!age || age < 50) return '50-';
  if (age < 65) return '50-65';
  if (age < 75) return '65-75';
  if (age < 85) return '75-85';
  return '85+';
}

function createTemporaryUserProfile(userId: string): UserProfile {
  return {
    id: userId,
    name: `用户_${userId.slice(-4)}`,
    ageRange: 'unknown',
    assessmentFor: 'self',
    preferredContentLevel: 'standard',
    source: 'default'
  };
}

export default UserContext;